/**
 * Who someone is, on hover or click of their picture or name: a card with the
 * picture, the name (with the robot mark for agents), and the facts that
 * matter. For yourself it links to your profile page.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { api } from "../lib/api";
import type { Person } from "../lib/store";
import { IconChevron, IconMail, IconRobot } from "./icons";
import { Avatar, ROLE_LABEL } from "./ui";

interface Card {
  id: string;
  name: string;
  kind: "human" | "agent";
  avatarUrl: string | null;
  role: "admin" | "member" | "guest";
  email: string | null;
  online: boolean;
  bio: string;
  joinedAt: string;
  isMe: boolean;
  agent: { kind: string; provider: string; model: string; specialty: string; createdBy: string | null; enabled: boolean; ownerOnly: boolean } | null;
}

const cache = new Map<string, { at: number; card: Card }>();

export function PersonTrigger({ person, children, className }: { person: Pick<Person, "id" | "name">; children: ReactNode; className?: string }) {
  const [open, setOpen] = useState<"hover" | "click" | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = () => timer.current && clearTimeout(timer.current);
  return (
    <>
      <span
        ref={anchor}
        className={`person-trigger${className ? ` ${className}` : ""}`}
        role="button"
        tabIndex={0}
        aria-label={`About ${person.name}`}
        onMouseEnter={() => {
          clear();
          timer.current = setTimeout(() => setOpen((o) => o ?? "hover"), 450);
        }}
        onMouseLeave={() => {
          clear();
          timer.current = setTimeout(() => setOpen((o) => (o === "hover" ? null : o)), 250);
        }}
        onClick={(e) => {
          e.stopPropagation();
          clear();
          setOpen((o) => (o === "click" ? null : "click"));
        }}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setOpen("click"))}
      >
        {children}
      </span>
      {open && anchor.current && (
        <ProfilePopover
          id={person.id}
          anchor={anchor.current}
          onClose={() => setOpen(null)}
          onHover={(inside) => {
            clear();
            if (!inside && open === "hover") timer.current = setTimeout(() => setOpen(null), 250);
          }}
        />
      )}
    </>
  );
}

function ProfilePopover({ id, anchor, onClose, onHover }: { id: string; anchor: HTMLElement; onClose: () => void; onHover: (inside: boolean) => void }) {
  const [card, setCard] = useState<Card | null>(cache.get(id)?.card ?? null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hit = cache.get(id);
    if (hit && Date.now() - hit.at < 30_000) return;
    api<Card>(`/api/people/${id}`).then((c) => (cache.set(id, { at: Date.now(), card: c }), setCard(c)), () => undefined);
  }, [id]);

  useLayoutEffect(() => {
    const r = anchor.getBoundingClientRect();
    const w = 320;
    const h = box.current?.offsetHeight ?? 260;
    const below = r.bottom + 8 + h < innerHeight;
    setPos({ left: Math.max(8, Math.min(r.left, innerWidth - w - 8)), top: below ? r.bottom + 8 : Math.max(8, r.top - h - 8) });
  }, [anchor, card]);

  useEffect(() => {
    const down = (e: MouseEvent) => !box.current?.contains(e.target as Node) && !anchor.contains(e.target as Node) && onClose();
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", down);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", down);
      document.removeEventListener("keydown", key);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div className="profile-card" ref={box} style={{ top: pos.top, left: pos.left }} role="dialog" aria-label="Profile" onMouseEnter={() => onHover(true)} onMouseLeave={() => onHover(false)}>
      <div className="pc-banner" />
      {!card ? (
        <p className="muted-text pc-loading">Loading</p>
      ) : (
        <>
          <div className="pc-avatar">
            <Avatar person={card} size={76} showOnline />
          </div>
          <div className="pc-name">
            <b>{card.name}</b>
            {card.kind === "agent" && <IconRobot size={16} />}
            <span className={`chip${card.online ? " ok" : ""}`}>{card.online ? "online" : card.kind === "agent" ? "offline" : "away"}</span>
          </div>
          <dl className="pc-facts">
            {card.kind === "agent" && card.agent ? (
              <>
                <dt>Specialty</dt>
                <dd>{card.agent.specialty.trim() || "Not set"}</dd>
                <dt>Intelligence</dt>
                <dd>{card.agent.model || "Not set"}</dd>
                <dt>Runs on</dt>
                <dd>{card.agent.provider}</dd>
                {card.agent.ownerOnly && (
                  <>
                    <dt>Who can ask</dt>
                    <dd>Only the workspace owner</dd>
                  </>
                )}
                {card.agent.createdBy && (
                  <>
                    <dt>Created by</dt>
                    <dd>{card.agent.createdBy}</dd>
                  </>
                )}
              </>
            ) : (
              <>
                <dt>Role</dt>
                <dd>{ROLE_LABEL[card.role]}</dd>
                {card.email && (
                  <>
                    <dt>
                      <IconMail /> Email
                    </dt>
                    <dd>
                      <a href={`mailto:${card.email}`}>{card.email}</a>
                    </dd>
                  </>
                )}
                {card.bio.trim() && (
                  <>
                    <dt>About</dt>
                    <dd className="pc-bio">{card.bio.trim().slice(0, 240)}</dd>
                  </>
                )}
              </>
            )}
          </dl>
          {card.isMe && (
            <Link className="pc-link" to="/profile" onClick={onClose}>
              <span>Your profile</span>
              <IconChevron />
            </Link>
          )}
        </>
      )}
    </div>,
    document.body,
  );
}
